import type {
  TeammatesFilterStatus,
  TeammatesFilterType,
} from './useTeammatesFilter';
import type {
  TeammatesInvitationFragment,
  TeammatesUserFragment,
} from '../../graphql/generated/graphql';
import {
  InvitationStatus,
  ProvisionType,
  UserType,
} from '../../graphql/generated/graphql';

export const getSearchFilter = (query = '') => {
  const lowercasedQuery = query.toLowerCase();

  return (
    teammate?: TeammatesUserFragment | TeammatesInvitationFragment | null,
  ) =>
    lowercasedQuery
      ? teammate?.name?.toLowerCase().includes(lowercasedQuery) ||
        teammate?.email?.toLowerCase().includes(lowercasedQuery)
      : true;
};

const getUsersTypeFilter = (types: TeammatesFilterType[]) => {
  if (types.length === 0) {
    return () => true;
  }
  const shouldBeAdmin = types.includes('ADMIN');
  const shouldBeGuest = types.includes('GUEST');
  const shouldBeUser = types.includes('USER');

  return (user?: TeammatesUserFragment | null) =>
    user &&
    ((shouldBeAdmin && user.type === UserType.Admin) ||
      (shouldBeGuest && user.type === UserType.Guest) ||
      (shouldBeUser && user.type === UserType.User));
};

const getUsersStatusFilter = (statuses: TeammatesFilterStatus[]) => {
  if (statuses.length === 0) {
    return () => true;
  }

  const shouldBeActive = statuses.includes('ACTIVE');
  const shouldBeDeactivated = statuses.includes('DEACTIVATED');

  return (user?: TeammatesUserFragment | null) =>
    user &&
    ((shouldBeActive && !user.deactivatedAt) ||
      (shouldBeDeactivated && user.deactivatedAt));
};

export const getUsersFilter = (
  types: TeammatesFilterType[],
  statuses: TeammatesFilterStatus[],
) => {
  const usersTypeFilter = getUsersTypeFilter(types);
  const usersStatusFilter = getUsersStatusFilter(statuses);

  return (user?: TeammatesUserFragment | null) =>
    usersTypeFilter(user) && usersStatusFilter(user);
};

const getInvitationsTypeFilter = (types: TeammatesFilterType[]) => {
  if (types.length === 0) {
    return () => true;
  }
  const shouldBeAdmin = types.includes('ADMIN');
  const shouldBeGuest = types.includes('GUEST');
  const shouldBeUser = types.includes('USER');

  return (invitation?: TeammatesInvitationFragment | null) =>
    invitation &&
    ((shouldBeAdmin && invitation.type === ProvisionType.Admin) ||
      (shouldBeGuest && invitation.type === ProvisionType.Guest) ||
      (shouldBeUser && invitation.type === ProvisionType.User));
};

const getInvitationsStatusFilter = (statuses: TeammatesFilterStatus[]) => {
  if (statuses.length === 0) {
    return () => true;
  }

  const shouldBeInvited = statuses.includes('INVITED');
  const shouldBePending = statuses.includes('PENDING');
  const shouldBeRejected = statuses.includes('REJECTED');

  return (invitation?: TeammatesInvitationFragment | null) =>
    invitation &&
    ((shouldBeInvited && invitation.status === InvitationStatus.Sent) ||
      (shouldBePending && invitation.status === InvitationStatus.Pending) ||
      (shouldBeRejected && invitation.status === InvitationStatus.Rejected));
};

export const getInvitationsFilter = (
  types: TeammatesFilterType[],
  statuses: TeammatesFilterStatus[],
) => {
  const invitationsTypeFilter = getInvitationsTypeFilter(types);
  const invitationsStatusFilter = getInvitationsStatusFilter(statuses);

  return (invitation?: TeammatesInvitationFragment | null) =>
    invitationsTypeFilter(invitation) && invitationsStatusFilter(invitation);
};
